import { useTranslation } from 'react-i18next';

import type { AccessGroup } from '@/api/auth';
import { Button } from '@/components/ui/Button';

import { mayAddCandidate } from './candidateAccess';

interface Props {
  groups: AccessGroup[];
  /**
   * What opening the dialog does — §6.4's `AddCandidateDialog`, which TASK-064 mounted **once** in
   * `index.tsx` so that both places §6.3 puts this button open the same dialog. It stays optional
   * because the group check, not the dialog, is what this component exists for: a call site that
   * renders the button without an action is a bug worth being able to write, and TASK-063 shipped
   * exactly that on purpose while the dialog did not exist.
   */
  onClick?: () => void;
  className?: string;
}

/**
 * §6.3's **Add candidate**, in both places §6.3 puts it: above the table, and as the one action of
 * the *"No candidates yet."* empty state.
 *
 * ⚠️ **Not rendered for an Interviewer — hidden, never disabled** (§5, and `candidateAccess.ts`
 * cites the matrix row that refuses them `POST /people/`). The group check lives in this component so
 * that both call sites inherit it rather than each remembering: an empty state whose action is a
 * button that answers 403 is worse than an empty state with no action.
 */
export function AddCandidateButton({ groups, onClick, className }: Props) {
  const { t } = useTranslation('candidates');

  if (!mayAddCandidate(groups)) return null;

  return (
    <Button onClick={onClick} className={className}>
      {t('add')}
    </Button>
  );
}
